import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight,
  Building2,
  CalendarClock,
  Check,
  Copy,
  Download,
  FileText,
  HeartHandshake,
  Loader2,
  Upload,
} from "lucide-react";
import { FadeUp, MaskedLines, Overline } from "@/components/Reveal";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORG = {
  name: "Asociația Art & Outdoor România",
  cif: "38948024",
  iban: "RO33 RNCB 0003 1859 9467 0001",
  bank: "Banca Comercială Română (BCR)",
};

const ANAF_FORM = "https://www.anaf.ro/anaf/internet/ANAF/servicii_online/declaratii_electronice/descarcare_declaratii";
const ANAF_SPV = "https://formularespv-pf.anaf.ro/";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-carddark px-4 py-3 text-sm text-neutral-100 placeholder:text-neutral-600 transition-colors duration-300 focus:border-neon focus:outline-none focus:ring-1 focus:ring-neon";

const labelCls =
  "mb-2 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-400";

const emptyForm = {
  nume: "",
  prenume: "",
  initiala_tatalui: "",
  cnp: "",
  strada: "",
  numar: "",
  bloc: "",
  scara: "",
  etaj: "",
  apartament: "",
  localitate: "",
  judet: "",
  cod_postal: "",
  telefon: "",
  email: "",
  doi_ani: true,
  acord_gdpr: false,
};

const PROJECTS_FUNDED = [
  {
    t: "Ateliere anti-bullying în școli",
    d: "Bushido vs. Bullying — sesiuni cu elevii, materiale și training pentru profesori.",
  },
  {
    t: "RISE 27'",
    d: "Festivalul de la Cetatea Alba Carolina: scene, ateliere creative și acces gratuit pentru tineri.",
  },
  {
    t: "Cupa Caritabilă",
    d: "Mini-campionatul de fotbal: echipament, arbitri, teren și premii pentru copii.",
  },
];

const Counter = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/form230/stats`)
      .then(({ data }) => setStats(data))
      .catch(() => setStats(null));
  }, []);

  const total = stats?.total ?? 0;
  const goal = stats?.goal ?? 100;
  const pct = Math.min(100, Math.round((total / goal) * 100));

  return (
    <div
      data-testid="form230-counter"
      className="rounded-3xl border border-white/10 bg-surface p-7 sm:p-10"
    >
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-neon">
            Formulare strânse
          </p>
          <p className="mt-3 font-display text-5xl font-black leading-none text-white sm:text-6xl">
            <span data-testid="counter-total">{total}</span>
            <span className="text-2xl text-neutral-600"> / {goal}</span>
          </p>
        </div>
        <p className="max-w-sm text-sm leading-relaxed text-neutral-400">
          Fiecare formular înseamnă câteva sute de lei care rămân în Alba Iulia,
          la copiii și tinerii din proiectele noastre.
        </p>
      </div>
      <div className="mt-7 h-2 w-full overflow-hidden rounded-full bg-white/10">
        <div
          data-testid="counter-progress"
          className="h-full rounded-full bg-neon transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-10 grid gap-6 sm:grid-cols-3">
        {PROJECTS_FUNDED.map((p) => (
          <div key={p.t} data-testid={`funded-${p.t.split(" ")[0].toLowerCase()}`}>
            <p className="font-display text-xs font-bold uppercase tracking-wide text-white">
              {p.t}
            </p>
            <p className="mt-2 text-xs leading-relaxed text-neutral-400">{p.d}</p>
          </div>
        ))}
      </div>
      <Link
        to="/transparenta"
        data-testid="counter-transparency-link"
        className="mt-8 inline-flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-neon transition-colors hover:text-white"
      >
        Vezi exact unde ajung banii
        <ArrowUpRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
};

const UploadCard = () => {
  const [files, setFiles] = useState([]);
  const [meta, setMeta] = useState({ full_name: "", email: "", phone: "", county: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setMeta((m) => ({ ...m, [key]: e.target.value }));

  const send = async (e) => {
    e.preventDefault();
    if (!files.length) {
      toast.error("Alege poza sau PDF-ul formularului semnat.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      Object.entries(meta).forEach(([k, v]) => fd.append(k, v));
      const { data } = await axios.post(`${API}/form230/submissions`, fd);
      setDone(true);
      setFiles([]);
      e.target.reset();
      setMeta({ full_name: "", email: "", phone: "", county: "" });
      toast.success(`Am primit ${data.uploaded} fișier(e). Mulțumim!`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Încărcarea a eșuat. Încearcă din nou.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      data-testid="form230-upload"
      onSubmit={send}
      className="rounded-3xl border border-white/10 bg-surface p-6 sm:p-9"
    >
      <p className="flex items-center gap-3 font-display text-sm font-black uppercase tracking-wide text-white">
        <Upload className="h-4 w-4 text-neon" /> Trimite-ne formularul semnat
      </p>
      <p className="mt-3 text-xs leading-relaxed text-neutral-400">
        Ai semnat formularul? Fotografiază-l sau scanează-l și încarcă-l aici —
        îl depunem noi la ANAF. Acceptăm JPG, PNG, HEIC sau PDF, până în 15 MB
        per fișier.
      </p>

      <div className="mt-7 grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls}>Nume complet *</label>
          <input data-testid="upload-name" required value={meta.full_name} onChange={set("full_name")} className={inputCls} placeholder="Ion Popescu" />
        </div>
        <div>
          <label className={labelCls}>Email *</label>
          <input data-testid="upload-email" type="email" required value={meta.email} onChange={set("email")} className={inputCls} placeholder="nume@email.com" />
        </div>
        <div>
          <label className={labelCls}>Telefon (opțional)</label>
          <input data-testid="upload-phone" value={meta.phone} onChange={set("phone")} className={inputCls} placeholder="07xx xxx xxx" />
        </div>
        <div>
          <label className={labelCls}>Județ</label>
          <input data-testid="upload-county" value={meta.county} onChange={set("county")} className={inputCls} placeholder="Alba" />
        </div>
      </div>

      <label className="mt-6 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-white/20 bg-carddark px-6 py-9 text-center transition-colors hover:border-neon">
        <Upload className="h-5 w-5 text-neon" />
        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-300">
          {files.length ? `${files.length} fișier(e) selectate` : "Alege poza / PDF-ul formularului"}
        </span>
        <input
          data-testid="upload-file-input"
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.webp,.heic,.pdf"
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
          className="hidden"
        />
      </label>

      {files.length > 0 && (
        <ul data-testid="upload-file-list" className="mt-4 space-y-1.5">
          {files.map((f) => (
            <li key={f.name} className="truncate font-mono text-[11px] text-neutral-400">
              {f.name} · {(f.size / 1024 / 1024).toFixed(2)} MB
            </li>
          ))}
        </ul>
      )}

      <button
        type="submit"
        data-testid="upload-submit"
        disabled={loading}
        className="mt-7 flex items-center gap-2 rounded-full bg-neon px-8 py-4 font-display text-xs font-bold uppercase tracking-wider text-canvas transition-colors duration-300 hover:bg-white disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        Trimite formularul
      </button>

      {done && (
        <p data-testid="upload-success" className="mt-5 flex items-center gap-2 text-xs font-bold text-neon">
          <Check className="h-4 w-4" /> Formularul a ajuns la noi. Te contactăm dacă lipsește ceva.
        </p>
      )}
    </form>
  );
};

const emptySponsor = {
  company_name: "",
  cui: "",
  reg_com: "",
  address: "",
  representative: "",
  representative_role: "Administrator",
  email: "",
  phone: "",
  amount: "",
  project: "Bushido vs. Bullying — ateliere anti-bullying",
  bank_account: "",
};

const SPONSOR_PROJECTS = [
  "Bushido vs. Bullying — ateliere anti-bullying",
  "RISE 27' — festival pentru tineri",
  "Cupa Caritabilă — mini-campionat de fotbal",
  "Proiectele generale ale asociației",
];

const SponsorshipForm = () => {
  const [form, setForm] = useState(emptySponsor);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, email: form.email.trim() || null };
      const res = await axios.post(`${API}/form230/sponsorship-pdf`, payload, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `contract-sponsorizare-${form.company_name.trim().toLowerCase().replace(/\s+/g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Contractul a fost descărcat. Printează-l în două exemplare.");
    } catch (err) {
      toast.error("Nu am putut genera contractul. Verifică datele și încearcă din nou.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      data-testid="sponsorship-form"
      onSubmit={submit}
      className="rounded-2xl border border-white/10 bg-surface p-6 sm:p-8"
    >
      <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">
        Generează contractul de sponsorizare
      </p>
      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls}>Denumire firmă *</label>
          <input data-testid="sponsor-company" required value={form.company_name} onChange={set("company_name")} className={inputCls} placeholder="SC Exemplu SRL" />
        </div>
        <div>
          <label className={labelCls}>CUI *</label>
          <input data-testid="sponsor-cui" required value={form.cui} onChange={set("cui")} className={inputCls} placeholder="RO12345678" />
        </div>
        <div>
          <label className={labelCls}>Nr. Registrul Comerțului</label>
          <input data-testid="sponsor-regcom" value={form.reg_com} onChange={set("reg_com")} className={inputCls} placeholder="J01/123/2020" />
        </div>
        <div>
          <label className={labelCls}>Cont bancar (IBAN)</label>
          <input data-testid="sponsor-iban" value={form.bank_account} onChange={set("bank_account")} className={inputCls} placeholder="RO.." />
        </div>
      </div>
      <div className="mt-5">
        <label className={labelCls}>Sediu social *</label>
        <input data-testid="sponsor-address" required value={form.address} onChange={set("address")} className={inputCls} placeholder="Str. Exemplu nr. 1, Alba Iulia, jud. Alba" />
      </div>
      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls}>Reprezentant legal *</label>
          <input data-testid="sponsor-rep" required value={form.representative} onChange={set("representative")} className={inputCls} placeholder="Ion Popescu" />
        </div>
        <div>
          <label className={labelCls}>Calitate</label>
          <input data-testid="sponsor-role" value={form.representative_role} onChange={set("representative_role")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input data-testid="sponsor-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="firma@email.com" />
        </div>
        <div>
          <label className={labelCls}>Telefon</label>
          <input data-testid="sponsor-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="07xx xxx xxx" />
        </div>
        <div>
          <label className={labelCls}>Sumă sponsorizare (lei) *</label>
          <input data-testid="sponsor-amount" required value={form.amount} onChange={set("amount")} className={inputCls} placeholder="5000" />
        </div>
        <div>
          <label className={labelCls}>Proiect susținut *</label>
          <select data-testid="sponsor-project" value={form.project} onChange={set("project")} className={inputCls}>
            {SPONSOR_PROJECTS.map((p) => (
              <option key={p} value={p} className="bg-carddark">
                {p}
              </option>
            ))}
          </select>
        </div>
      </div>
      <button
        type="submit"
        data-testid="sponsorship-submit"
        disabled={loading}
        className="mt-7 flex items-center gap-2 rounded-full bg-neon px-7 py-3.5 font-display text-[11px] font-bold uppercase tracking-wider text-canvas transition-colors hover:bg-white disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        Descarcă contractul precompletat
      </button>
      <p className="mt-4 text-[11px] leading-relaxed text-neutral-500">
        Contractul se printează în două exemplare, se semnează de ambele părți și
        se păstrează pentru deducerea fiscală. Îl primim și noi, semnat, pe email.
      </p>
    </form>
  );
};

const STEPS = [  {
    t: "Completezi datele",
    d: "Numele, CNP-ul și adresa, exact ca în buletin. Datele nu se salvează la noi — se folosesc doar ca să generăm PDF-ul în acest moment.",
  },
  {
    t: "Descarci și semnezi",
    d: "Primești Formularul 230 precompletat cu datele asociației (denumire, CIF, IBAN). Îl printezi și îl semnezi cu pix albastru.",
  },
  {
    t: "Îl depui",
    d: "Îl poți trimite nouă pe email sau prin poștă și îl depunem noi, îl duci la registratura ANAF, sau îl încarci singur în SPV.",
  },
];

const CopyField = ({ label, value, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Copiat.");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Nu am putut copia. Selectează textul manual.");
    }
  };
  return (
    <div className="flex items-start justify-between gap-4 border-b border-white/5 py-4 last:border-0">
      <div>
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">
          {label}
        </p>
        <p data-testid={testid} className="mt-1.5 font-display text-sm font-bold text-white">
          {value}
        </p>
      </div>
      <button
        type="button"
        onClick={copy}
        data-testid={`${testid}-copy`}
        aria-label={`Copiază ${label}`}
        className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 text-neutral-400 transition-colors hover:border-neon hover:text-neon"
      >
        {copied ? <Check className="h-4 w-4 text-neon" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
};

export default function Redirectioneaza() {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) =>
    setForm((f) => ({
      ...f,
      [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{13}$/.test(form.cnp.trim())) {
      toast.error("CNP-ul trebuie să aibă exact 13 cifre.");
      return;
    }
    if (!form.acord_gdpr) {
      toast.error("Bifează acordul pentru prelucrarea datelor.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, email: form.email.trim() || null };
      const res = await axios.post(`${API}/form230/pdf`, payload, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `formular-230-${form.nume.trim().toLowerCase().replace(/\s+/g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Formularul 230 a fost descărcat. Printează-l și semnează-l.");
    } catch (err) {
      toast.error("Nu am putut genera formularul. Verifică datele și încearcă din nou.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-12 lg:py-28">
      <FadeUp>
        <Overline>Susține-ne fără să te coste nimic</Overline>
      </FadeUp>
      <h1 className="mt-6 font-display text-4xl font-black uppercase leading-[0.92] tracking-tight text-white sm:text-5xl lg:text-6xl">
        <MaskedLines lines={["Redirecționează", "3,5% din impozit"]} />
      </h1>
      <FadeUp delay={0.1}>
        <p
          data-testid="redirect-intro"
          className="mt-8 max-w-2xl text-base leading-relaxed text-neutral-300"
        >
          Statul reține deja impozitul pe venitul tău. Prin Formularul 230 alegi
          ca până la 3,5% din acel impozit să ajungă la noi, în loc să rămână la
          buget. Nu plătești nimic în plus — banii merg direct în proiectele
          pentru copii și tineri: ateliere anti-bullying, evenimente și activități
          în aer liber.
        </p>
      </FadeUp>

      <FadeUp delay={0.15}>
        <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-carddark px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-neutral-300">
          <CalendarClock className="h-3.5 w-3.5 text-neon" />
          Termen: 25 mai, pentru veniturile anului precedent
        </div>
      </FadeUp>

      <FadeUp delay={0.18}>
        <div className="mt-14">
          <Counter />
        </div>
      </FadeUp>

      <div className="mt-20 grid gap-10 lg:grid-cols-[1.15fr_0.85fr]">
        <FadeUp delay={0.2}>
          <form
            data-testid="form230-form"
            onSubmit={submit}
            className="rounded-3xl border border-white/10 bg-surface p-6 sm:p-9"
          >
            <p className="flex items-center gap-3 font-display text-sm font-black uppercase tracking-wide text-white">
              <FileText className="h-4 w-4 text-neon" /> Generează Formularul 230
            </p>
            <p className="mt-3 text-xs leading-relaxed text-neutral-400">
              Completează datele tale. Datele asociației (denumire, CIF, IBAN)
              sunt deja trecute în formular.
            </p>

            <div className="mt-7 grid gap-5 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Nume *</label>
                <input data-testid="form230-nume" required value={form.nume} onChange={set("nume")} className={inputCls} placeholder="Popescu" />
              </div>
              <div>
                <label className={labelCls}>Prenume *</label>
                <input data-testid="form230-prenume" required value={form.prenume} onChange={set("prenume")} className={inputCls} placeholder="Ion" />
              </div>
              <div>
                <label className={labelCls}>Inițiala tatălui</label>
                <input data-testid="form230-initiala" value={form.initiala_tatalui} onChange={set("initiala_tatalui")} className={inputCls} placeholder="V" maxLength={5} />
              </div>
              <div>
                <label className={labelCls}>CNP *</label>
                <input data-testid="form230-cnp" required value={form.cnp} onChange={set("cnp")} className={inputCls} placeholder="13 cifre" inputMode="numeric" maxLength={13} />
              </div>
            </div>

            <div className="mt-7 grid gap-5 sm:grid-cols-[2fr_1fr_1fr]">
              <div>
                <label className={labelCls}>Strada</label>
                <input data-testid="form230-strada" value={form.strada} onChange={set("strada")} className={inputCls} placeholder="Str. Mihai Viteazu" />
              </div>
              <div>
                <label className={labelCls}>Număr</label>
                <input data-testid="form230-numar" value={form.numar} onChange={set("numar")} className={inputCls} placeholder="12" />
              </div>
              <div>
                <label className={labelCls}>Bloc</label>
                <input data-testid="form230-bloc" value={form.bloc} onChange={set("bloc")} className={inputCls} placeholder="B2" />
              </div>
            </div>

            <div className="mt-5 grid gap-5 sm:grid-cols-3">
              <div>
                <label className={labelCls}>Scara</label>
                <input data-testid="form230-scara" value={form.scara} onChange={set("scara")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Etaj</label>
                <input data-testid="form230-etaj" value={form.etaj} onChange={set("etaj")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Apartament</label>
                <input data-testid="form230-apartament" value={form.apartament} onChange={set("apartament")} className={inputCls} />
              </div>
            </div>

            <div className="mt-5 grid gap-5 sm:grid-cols-3">
              <div>
                <label className={labelCls}>Localitate *</label>
                <input data-testid="form230-localitate" required value={form.localitate} onChange={set("localitate")} className={inputCls} placeholder="Alba Iulia" />
              </div>
              <div>
                <label className={labelCls}>Județ / Sector *</label>
                <input data-testid="form230-judet" required value={form.judet} onChange={set("judet")} className={inputCls} placeholder="Alba" />
              </div>
              <div>
                <label className={labelCls}>Cod poștal</label>
                <input data-testid="form230-cod-postal" value={form.cod_postal} onChange={set("cod_postal")} className={inputCls} placeholder="510000" />
              </div>
            </div>

            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Telefon</label>
                <input data-testid="form230-telefon" value={form.telefon} onChange={set("telefon")} className={inputCls} placeholder="07xx xxx xxx" />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input data-testid="form230-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="nume@email.com" />
              </div>
            </div>

            <div className="mt-8 rounded-2xl border border-neon/40 bg-neon/[0.06] p-5">
              <label className="flex cursor-pointer items-start gap-3 text-xs font-bold leading-relaxed text-white">
                <input
                  data-testid="form230-doi-ani"
                  type="checkbox"
                  checked={form.doi_ani}
                  onChange={set("doi_ani")}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#CCFF00]"
                />
                Redirecționez pe 2 ani (recomandat — nu mai completezi formularul și anul viitor)
              </label>
            </div>

            <label className="mt-5 flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-neutral-300">
              <input
                data-testid="form230-gdpr"
                type="checkbox"
                checked={form.acord_gdpr}
                onChange={set("acord_gdpr")}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[#CCFF00]"
              />
              Sunt de acord ca datele introduse să fie folosite pentru a genera
              Formularul 230. Datele nu sunt stocate pe site — PDF-ul se creează
              în acest moment și se descarcă direct la tine.
            </label>

            <button
              type="submit"
              data-testid="form230-submit"
              disabled={loading}
              className="mt-8 flex items-center gap-2 rounded-full bg-neon px-8 py-4 font-display text-xs font-bold uppercase tracking-wider text-canvas transition-colors duration-300 hover:bg-white disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Descarcă formularul precompletat
            </button>
          </form>
        </FadeUp>

        <div className="space-y-8">
          <FadeUp delay={0.25}>
            <div
              data-testid="org-details-card"
              className="rounded-3xl border border-white/10 bg-surface p-6 sm:p-8"
            >
              <p className="flex items-center gap-3 font-display text-sm font-black uppercase tracking-wide text-white">
                <Building2 className="h-4 w-4 text-neon" /> Datele asociației
              </p>
              <div className="mt-5">
                <CopyField label="Denumire" value={ORG.name} testid="org-name" />
                <CopyField label="Cod de identificare fiscală (CIF)" value={ORG.cif} testid="org-cif" />
                <CopyField label="Cont bancar (IBAN)" value={ORG.iban} testid="org-iban" />
                <CopyField label="Bancă" value={ORG.bank} testid="org-bank" />
              </div>
              <p className="mt-5 text-xs leading-relaxed text-neutral-500">
                Folosește aceleași date și dacă vrei să donezi direct prin
                transfer bancar, cu mențiunea „donație”.
              </p>
            </div>
          </FadeUp>

          <FadeUp delay={0.3}>
            <div className="rounded-3xl border border-white/10 bg-surface p-6 sm:p-8">
              <p className="font-display text-sm font-black uppercase tracking-wide text-white">
                Cum ajunge la ANAF
              </p>
              <ol className="mt-6 space-y-6">
                {STEPS.map((s, i) => (
                  <li key={s.t} className="flex gap-4">
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-neon/40 font-mono text-[11px] font-bold text-neon">
                      {i + 1}
                    </span>
                    <div>
                      <p className="font-display text-xs font-bold uppercase tracking-wide text-white">
                        {s.t}
                      </p>
                      <p className="mt-1.5 text-xs leading-relaxed text-neutral-400">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="mt-8 flex flex-col gap-3">
                <a
                  href={ANAF_SPV}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="anaf-spv-link"
                  className="group flex items-center justify-between gap-3 rounded-full border border-white/15 px-5 py-3 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-300 transition-colors hover:border-neon hover:text-neon"
                >
                  Depune online în SPV (ANAF)
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <a
                  href={ANAF_FORM}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="anaf-form-link"
                  className="group flex items-center justify-between gap-3 rounded-full border border-white/15 px-5 py-3 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-300 transition-colors hover:border-neon hover:text-neon"
                >
                  Formularul oficial ANAF (PDF inteligent)
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
              <p className="mt-5 text-[11px] leading-relaxed text-neutral-500">
                Pentru depunerea online în SPV, ANAF cere formularul oficial
                „PDF inteligent”, validat din Adobe Reader. PDF-ul generat de noi
                este pentru semnare pe hârtie: la registratură, prin poștă sau
                trimis nouă ca să îl depunem noi.
              </p>
            </div>
          </FadeUp>
        </div>
      </div>

      <FadeUp delay={0.15}>
        <div className="mt-10">
          <UploadCard />
        </div>
      </FadeUp>

      <FadeUp delay={0.2}>
        <div
          data-testid="sponsorizare-firme"
          className="mt-24 rounded-3xl border border-white/10 bg-carddark p-7 sm:p-10"
        >
          <p className="flex items-center gap-3 font-display text-sm font-black uppercase tracking-wide text-neon">
            <HeartHandshake className="h-4 w-4" /> Pentru firme — sponsorizare din impozit
          </p>
          <h2 className="mt-5 max-w-3xl font-display text-2xl font-black uppercase leading-tight tracking-tight text-white sm:text-3xl">
            Firmele pot susține proiectele din impozitul pe profit
          </h2>
          <div className="mt-7 grid gap-8 md:grid-cols-2">
            <div className="space-y-4 text-sm leading-relaxed text-neutral-300">
              <p>
                Companiile plătitoare de impozit pe profit pot sponsoriza
                asociația și pot scădea suma din impozitul datorat, în limita
                legală (20% din impozitul pe profit, fără a depăși 0,75% din
                cifra de afaceri). Microîntreprinderile pot deduce sponsorizarea
                în limita a 20% din impozitul pe veniturile microîntreprinderilor.
              </p>
              <p>
                Sumele rămase neutilizate pot fi redirecționate către asociație
                prin formularul ANAF dedicat (D177), în termen de 6 luni de la
                depunerea declarației anuale.
              </p>
              <p className="text-neutral-400">
                Scrie-ne și pregătim contractul de sponsorizare, adaptat
                proiectului pe care vrei să îl susții: ateliere anti-bullying în
                școli, festivalul RISE 27' sau mini-campionatul caritabil.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-surface p-6">
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">
                Date pentru contract și plată
              </p>
              <div className="mt-4 space-y-3 text-sm text-neutral-300">
                <p data-testid="sponsor-org-name" className="font-display font-bold text-white">{ORG.name}</p>
                <p>CIF: <span data-testid="sponsor-org-cif" className="text-white">{ORG.cif}</span></p>
                <p>IBAN: <span data-testid="sponsor-org-iban" className="text-white">{ORG.iban}</span></p>
                <p>{ORG.bank}</p>
                <p>Sediu: Str. Ștefan Luchian nr. 3, ap. 10, Alba Iulia, jud. Alba</p>
              </div>
              <a
                href="mailto:contact@artandoutdoor.com?subject=Sponsorizare%20din%20impozitul%20pe%20profit"
                data-testid="sponsor-contact-link"
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-300 transition-colors hover:border-neon hover:text-neon"
              >
                Scrie-ne pe email
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
          <div className="mt-10">
            <SponsorshipForm />
          </div>
        </div>
      </FadeUp>
    </div>
  );
}
